import React, { useState, useEffect, useRef } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import { isMobile } from "react-device-detect";
import { v4 as uuidv4 } from "uuid";
import Drawer from "../../components/shared/Drawer";

const GRID_SIZE = 5;
const ITEM_TYPE = "OBJECT";

const scenarios = {
	vehicle_maintenance: {
		title: "L’entretien du véhicule avant le départ",
		objective:
			"Identifier et corriger les éléments de sécurité avant de prendre la route.",
		items: [
			{
				id: uuidv4(),
				text: "Essuie-glaces (1,1)",
				correctPosition: [1, 1],
			},
			{
				id: uuidv4(),
				text: "Pneus (2,2)",
				correctPosition: [2, 2],
				defaultPosition: [0, 1],
			},
			{
				id: uuidv4(),
				text: "Phares (3,3)",
				correctPosition: [3, 3],
			},
		],
	},
};

const Item = ({ item }) => {
	const [{ isDragging }, drag] = useDrag(() => ({
		type: ITEM_TYPE,
		item,
		collect: (monitor) => ({
			isDragging: !!monitor.isDragging(),
		}),
	}));

	return (
		<div
			ref={drag}
			style={{ opacity: isDragging ? 0.5 : 1 }}
			className="p-2 bg-blue-500 text-white rounded cursor-pointer overflow-hidden"
		>
			{item.text}
		</div>
	);
};

const GridCell = ({ x, y, item, onDrop, onRemove }) => {
	const [{ isOver }, drop] = useDrop(() => ({
		accept: ITEM_TYPE,
		drop: (draggedItem) => onDrop(x, y, draggedItem),
		collect: (monitor) => ({
			isOver: !!monitor.isOver(),
		}),
	}));

	return (
		<div
			ref={drop}
			onClick={() => item && onRemove(x, y)}
			className={`w-full h-full overflow-hidden flex items-center justify-center border aspect-square ${
				isOver ? "bg-gray-300" : "bg-transparent"
			}`}
		>
			{item ? (
				<Item item={item} />
			) : (
				<span className="text-gray-400">
					{x},{y}
				</span>
			)}
		</div>
	);
};

const PuzzleGame = () => {
	const [selectedScenario, setSelectedScenario] = useState(
		"vehicle_maintenance"
	);
	const [grid, setGrid] = useState(
		Array(GRID_SIZE)
			.fill(null)
			.map(() => Array(GRID_SIZE).fill(null))
	);
	const [availableItems, setAvailableItems] = useState(
		scenarios[selectedScenario].items.filter((item) => !item.defaultPosition)
	);
	const gridRef = useRef(null);
	const [gridSize, setGridSize] = useState(0);
	const [backgroundImage, setBackgroundImage] = useState(null);
	const [gridBackgroundImage, setGridBackgroundImage] = useState(null);
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);

	// Resize grid
	useEffect(() => {
		const updateSize = () => {
			if (gridRef.current) {
				const size = Math.min(window.innerWidth, window.innerHeight - 200) * 1;
				setGridSize(size);
			}
		};
		updateSize();
		window.addEventListener("resize", updateSize);
		return () => window.removeEventListener("resize", updateSize);
	}, []);

	//load new scenario
	useEffect(() => {
		setGrid((prevGrid) => {
			const newGrid = prevGrid.map((row) => [...row]);
			scenarios[selectedScenario].items.forEach((item) => {
				if (item.defaultPosition) {
					const [x, y] = item.defaultPosition;
					newGrid[x][y] = item;
				}
			});
			return newGrid;
		});
	}, [selectedScenario]);

	const Item = ({ item }) => {
		const [{ isDragging }, drag] = useDrag(() => ({
			type: ITEM_TYPE,
			item,
			collect: (monitor) => ({
				isDragging: !!monitor.isDragging(),
			}),
		}));

		return (
			<div
				ref={drag} // ✅ Make grid items draggable
				style={{ opacity: isDragging ? 0.5 : 1 }}
				className="p-2 bg-blue-500 text-white rounded cursor-pointer overflow-hidden"
			>
				{item.text}
			</div>
		);
	};

	const GridCell = ({ x, y, item, onDrop, onRemove }) => {
		const [{ isOver }, drop] = useDrop(() => ({
			accept: ITEM_TYPE,
			drop: (draggedItem) => onDrop(x, y, draggedItem),
			collect: (monitor) => ({
				isOver: !!monitor.isOver(),
			}),
		}));

		return (
			<div
				ref={drop}
				onClick={() => item && onRemove(x, y)}
				className={`w-full h-full overflow-hidden flex items-center justify-center border aspect-square ${
					isOver ? "bg-gray-300" : "bg-transparent"
				}`}
			>
				{item ? (
					<Item item={item} />
				) : (
					<span className="text-gray-400">
						{x},{y}
					</span>
				)}
			</div>
		);
	};

	const handleDrop = (x, y, draggedItem) => {
		setGrid((prevGrid) => {
			const newGrid = prevGrid.map((row) => [...row]);

			// ✅ Find and remove the item from the old position
			for (let i = 0; i < GRID_SIZE; i++) {
				for (let j = 0; j < GRID_SIZE; j++) {
					if (newGrid[i][j]?.id === draggedItem.id) {
						newGrid[i][j] = null;
					}
				}
			}

			// ✅ Place the item in the new cell
			if (newGrid[x][y]) {
				setAvailableItems((prev) => [...prev, newGrid[x][y]]);
			}
			newGrid[x][y] = draggedItem;

			// ✅ Remove the item from available items if dragged from inventory
			setAvailableItems((prev) =>
				prev.filter((item) => item.id !== draggedItem.id)
			);

			return newGrid;
		});
	};

	const handleRemove = (x, y) => {
		if (grid[x][y]) {
			setAvailableItems((prev) => [...prev, grid[x][y]]);
		}
		setGrid((prevGrid) => {
			const newGrid = prevGrid.map((row) => [...row]);
			if (newGrid[x][y]) {
				newGrid[x][y] = null;
			}
			return newGrid;
		});
	};

	const submitAnswer = () => {
		let errors = 0;
		scenarios[selectedScenario].items.forEach((item) => {
			if (item.correctPosition) {
				const [correctX, correctY] = item.correctPosition;
				if (
					!grid[correctX][correctY] ||
					grid[correctX][correctY].id !== item.id
				) {
					errors++;
				}
			}
		});
		if (availableItems.length > 0) {
			errors += availableItems.length;
		}
		alert(
			errors === 0
				? "Correct! All items are placed correctly."
				: `You have ${errors} errors. Try again!`
		);
	};

	return (
		<DndProvider
			backend={isMobile ? TouchBackend : HTML5Backend}
			options={{ enableMouseEvents: true }}
		>
			<div
				className="relative flex flex-col items-center h-screen w-screen"
				style={{
					background: backgroundImage ? `url(${backgroundImage})` : "none",
					backgroundSize: "cover",
					backgroundPosition: "center",
				}}
			>
				<div className="p-6 bg-white w-full rounded-b-3xl ">
					<h1 className="text-md font-bold">
						{scenarios[selectedScenario].title}
					</h1>
					<p>{scenarios[selectedScenario].objective}</p>

					<button
						onClick={() => setIsDrawerOpen(true)}
						className="absolute top-4 right-4 bg-black rounded-full px-4 py-2 text-white hover:bg-gray-800"
					>
						Settings
					</button>
					<Drawer
						isOpen={isDrawerOpen}
						onClose={() => setIsDrawerOpen(false)}
						onBackgroundChange={setBackgroundImage}
						onGridBackgroundChange={setGridBackgroundImage}
					/>
				</div>
				<div
					ref={gridRef}
					className="grid grid-cols-5 grid-rows-5 gap-0  m-auto w-full aspect-square border-black border-8 shadow-[5px_5px_0px_rgba(0,0,0,1)]"
					style={{
						width: gridSize,
						height: gridSize,
						backgroundImage: gridBackgroundImage
							? `url(${gridBackgroundImage})`
							: "none",
						backgroundSize: "cover",
					}}
				>
					{grid.map((row, x) =>
						row.map((cell, y) => (
							<GridCell
								key={`${x}-${y}`}
								x={x}
								y={y}
								item={cell}
								onDrop={handleDrop}
								onRemove={handleRemove}
							/>
						))
					)}
				</div>

				<div className="p-6 bg-white w-full rounded-t-3xl ">
					<div className="flex flex-wrap gap-2">
						{availableItems.map((item) => (
							<Item key={item.id} item={item} />
						))}
						<button
							className="bg-black rounded-full px-4 py-2 text-white hover:bg-gray-800"
							onClick={() => submitAnswer()}
						>
							Valider
						</button>
					</div>
				</div>
			</div>
		</DndProvider>
	);
};

export default PuzzleGame;
